import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { operationsApi } from '@/features/workspace/api';
import type { OperationsFile } from '@/features/operations/types';

export const OperationsFilesView: React.FC = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState<OperationsFile[]>([]);
  const [kpis, setKpis] = useState({
    requests_with_files: 4,
    verified: 12,
    new_versions: 3,
    clarification: 1,
  });
  const [search, setSearch] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [checkingFile, setCheckingFile] = useState<OperationsFile | null>(null);
  const [checkStatus, setCheckStatus] = useState<string>('VERIFIED');
  const [completeness, setCompleteness] = useState<string>('COMPLETE');
  const [comment, setComment] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const res = await operationsApi.module('files', {
        search: search || undefined,
      });
      const data = res as {
        items: OperationsFile[];
        kpis: {
          requests_with_files: number;
          verified: number;
          new_versions: number;
          clarification: number;
        };
      };
      setFiles(data.items || []);
      if (data.kpis) {
        setKpis(data.kpis);
      }
    } catch {
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [search]);

  const handleOpenCheck = (file: OperationsFile) => {
    setCheckingFile(file);
    setCheckStatus(file.status);
    setCompleteness(file.completeness || 'COMPLETE');
    setComment(file.review_comment || '');
  };

  const handleSaveCheck = async () => {
    if (!checkingFile) return;
    try {
      setSubmitting(true);
      await operationsApi.checkFile(checkingFile.id, {
        status: checkStatus,
        completeness,
        review_comment: comment,
      });
      setCheckingFile(null);
      await fetchFiles();
    } catch {
      alert('Ошибка при сохранении статуса проверки файла');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'VERIFIED':
        return { label: 'Проверено', bg: '#059669', color: '#ffffff' };
      case 'NEW_VERSION':
        return { label: 'Новая версия', bg: '#0284c7', color: '#ffffff' };
      case 'NEEDS_CLARIFICATION':
        return { label: 'Нужно уточнение', bg: '#d97706', color: '#ffffff' };
      case 'DELIVERED':
        return { label: 'Передано', bg: '#7c3aed', color: '#ffffff' };
      default:
        return { label: status, bg: '#334155', color: '#f8fafc' };
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            type="button"
            onClick={() => navigate('/app/operations')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: '#1e293b',
              border: '1px solid #334155',
              color: '#94a3b8',
              cursor: 'pointer',
              fontSize: '1rem',
            }}
          >
            ←
          </button>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f8fafc', margin: 0 }}>
              Документы и версии заявок
            </h1>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.875rem' }}>
              Контроль версий, комплектности и структуры папок Google Drive
            </p>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
        <div
          style={{
            backgroundColor: '#0f172a',
            border: '1px solid #1e293b',
            borderRadius: '10px',
            padding: '1.25rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          <span style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Заявок с файлами
          </span>
          <span style={{ fontSize: '1.875rem', fontWeight: 700, color: '#38bdf8' }}>
            {kpis.requests_with_files}
          </span>
        </div>
        <div
          style={{
            backgroundColor: '#0f172a',
            border: '1px solid #1e293b',
            borderRadius: '10px',
            padding: '1.25rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          <span style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Проверено
          </span>
          <span style={{ fontSize: '1.875rem', fontWeight: 700, color: '#10b981' }}>
            {kpis.verified}
          </span>
        </div>
        <div
          style={{
            backgroundColor: '#0f172a',
            border: '1px solid #1e293b',
            borderRadius: '10px',
            padding: '1.25rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          <span style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Новые версии
          </span>
          <span style={{ fontSize: '1.875rem', fontWeight: 700, color: '#a855f7' }}>
            {kpis.new_versions}
          </span>
        </div>
        <div
          style={{
            backgroundColor: '#0f172a',
            border: '1px solid #1e293b',
            borderRadius: '10px',
            padding: '1.25rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          <span style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Уточнения
          </span>
          <span style={{ fontSize: '1.875rem', fontWeight: 700, color: '#fbbf24' }}>
            {kpis.clarification}
          </span>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#0f172a',
          padding: '1rem',
          borderRadius: '10px',
          border: '1px solid #1e293b',
        }}
      >
        <div style={{ width: '100%', maxWidth: '360px' }}>
          <input
            type="text"
            placeholder="Поиск по файлу, коду заявки, автору..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem 0.875rem',
              backgroundColor: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '6px',
              color: '#f8fafc',
              fontSize: '0.8125rem',
              outline: 'none',
            }}
          />
        </div>
      </div>

      <div
        style={{
          backgroundColor: '#0f172a',
          border: '1px solid #1e293b',
          borderRadius: '10px',
          overflowX: 'auto',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '950px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #1e293b', backgroundColor: '#0b1120' }}>
              <th style={{ padding: '0.875rem 1rem', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                Заявка
              </th>
              <th style={{ padding: '0.875rem 1rem', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                Файл
              </th>
              <th style={{ padding: '0.875rem 1rem', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                Версия
              </th>
              <th style={{ padding: '0.875rem 1rem', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                Автор
              </th>
              <th style={{ padding: '0.875rem 1rem', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                Статус проверки
              </th>
              <th style={{ padding: '0.875rem 1rem', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                Расположение Google Drive
              </th>
              <th style={{ padding: '0.875rem 1rem', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', textAlign: 'right' }}>
                Действие
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>
                  Загрузка файлов...
                </td>
              </tr>
            ) : files.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                  Файлы не найдены
                </td>
              </tr>
            ) : (
              files.map((f) => {
                const badge = getStatusBadge(f.status);
                return (
                  <tr
                    key={f.id}
                    style={{
                      borderBottom: '1px solid #1e293b',
                    }}
                  >
                    <td style={{ padding: '1rem', color: '#38bdf8', fontWeight: 600, fontSize: '0.875rem' }}>
                      {f.request_code}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ color: '#f8fafc', fontWeight: 500, fontSize: '0.875rem' }}>
                        📄 {f.filename}
                      </div>
                      {f.review_comment && (
                        <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '4px' }}>
                          {f.review_comment}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span
                        style={{
                          backgroundColor: '#1e293b',
                          color: '#38bdf8',
                          padding: '0.2rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          border: '1px solid #334155',
                        }}
                      >
                        {f.version_label}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', color: '#cbd5e1', fontSize: '0.8125rem' }}>
                      {f.author_label}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.625rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: badge.bg,
                          color: badge.color,
                        }}
                      >
                        {badge.label}
                      </span>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div
                        style={{
                          maxWidth: '380px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: '#64748b',
                          fontSize: '0.75rem',
                        }}
                        title={f.drive_path}
                      >
                        📁 {f.drive_path}
                      </div>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => handleOpenCheck(f)}
                        style={{
                          padding: '0.375rem 0.875rem',
                          backgroundColor: '#1e293b',
                          border: '1px solid #334155',
                          borderRadius: '6px',
                          color: '#38bdf8',
                          fontSize: '0.8125rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                      >
                        Проверить
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {checkingFile && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(4px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
          }}
          onClick={() => setCheckingFile(null)}
        >
          <div
            style={{
              backgroundColor: '#0f172a',
              border: '1px solid #334155',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '520px',
              padding: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1.25rem',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <div style={{ fontSize: '0.8125rem', color: '#94a3b8', textTransform: 'uppercase' }}>
                Операционная проверка файла
              </div>
              <div style={{ fontSize: '1.125rem', fontWeight: 600, color: '#f8fafc', marginTop: '4px' }}>
                {checkingFile.filename} ({checkingFile.request_code})
              </div>
            </div>

            <div>
              <label style={{ display: 'block', color: '#cbd5e1', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>
                Статус проверки:
              </label>
              <select
                value={checkStatus}
                onChange={(e) => setCheckStatus(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.625rem',
                  backgroundColor: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  color: '#f8fafc',
                  fontSize: '0.875rem',
                }}
              >
                <option value="VERIFIED">Проверено (принято)</option>
                <option value="NEEDS_CLARIFICATION">Нужно уточнение (запросить замену)</option>
                <option value="NEW_VERSION">Новая версия (на доработке)</option>
                <option value="DELIVERED">Передано заявителю</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', color: '#cbd5e1', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>
                Комплектность:
              </label>
              <select
                value={completeness}
                onChange={(e) => setCompleteness(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.625rem',
                  backgroundColor: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  color: '#f8fafc',
                  fontSize: '0.875rem',
                }}
              >
                <option value="COMPLETE">Полный комплект (Complete)</option>
                <option value="PARTIAL">Частичный комплект (Partial)</option>
                <option value="MISSING">Отсутствуют обязательные файлы (Missing)</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', color: '#cbd5e1', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>
                Комментарий оператора:
              </label>
              <textarea
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Укажите результаты проверки или необходимые правки..."
                style={{
                  width: '100%',
                  padding: '0.625rem',
                  backgroundColor: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  color: '#f8fafc',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setCheckingFile(null)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#334155',
                  color: '#f8fafc',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                }}
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleSaveCheck}
                disabled={submitting}
                style={{
                  padding: '0.5rem 1.25rem',
                  backgroundColor: '#0284c7',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
